import type { ReferenceDataRecordVersion } from "@/types/reference-data";
import { operationLabel } from "@/lib/reference-data-labels";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Numeric major of a version string (`"2.3"` → 2), or `null` when unparseable.
 *
 *  Compared as a number, never as text: `"10.0" < "2.0"` lexicographically, and
 *  the majors do reach two digits on the busier ref_* keys. Mirrors the API's
 *  `split_part(version, '.', 1)::int`.
 */
function major(version: string | null): number | null {
  if (!version) return null;
  const parsed = Number.parseInt(version.split(".")[0], 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** The versions offered in the "revert this row" dialog: the row's lineage minus
 *  the one already live, newest first.
 *
 *  The API already filters to versions whose run reached active (a
 *  never-promoted draft would bypass the ingest sanity gate), so this only has
 *  to drop the active row and order the rest. A version whose number cannot be
 *  parsed is kept — last — rather than hidden: it is a real row the user may
 *  still want, and silently dropping it would look like data loss.
 */
export function rollbackOptions(
  items: ReferenceDataRecordVersion[],
): ReferenceDataRecordVersion[] {
  return items
    .filter((item) => !item.is_active)
    .slice()
    .sort((a, b) => {
      const [ma, mb] = [major(a.version), major(b.version)];
      if (ma === null && mb === null) return 0;
      if (ma === null) return 1;
      if (mb === null) return -1;
      return mb - ma;
    });
}

/** Short label for one version option: `"v2.0 · edit"`.
 *
 *  `t` is optional so the pure-string callers keep working; passing it
 *  translates both the missing-version wording and the operation name, which
 *  are user-visible in the "revert this row" dialog.
 */
export function versionLabel(item: ReferenceDataRecordVersion, t?: Translate): string {
  if (!item.version) return t ? t("unknown version") : "unknown version";
  if (!item.operation_type) return `v${item.version}`;
  const op = t ? operationLabel(t, item.operation_type) : item.operation_type;
  return `v${item.version} · ${op}`;
}

/** The row's natural key as `col=value` pairs, for the dialog title.
 *
 *  A NULL key column renders as `∅`, not as an empty string: 5 ref_* tables have
 *  a nullable key column, and `col=` would read as "the empty string" when it
 *  actually means "no value" — the same distinction the `NULLS NOT DISTINCT`
 *  indexes encode.
 */
export function keyLabel(
  naturalKey: string[],
  item: ReferenceDataRecordVersion,
): string {
  return naturalKey
    .map((col) => {
      const value = item.values[col];
      return `${col}=${value === null || value === undefined ? "∅" : String(value)}`;
    })
    .join(" · ");
}
